import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Banknote,
  BarChart2,
  Bell,
  BellRing,
  Blocks,
  Bot,
  Brain,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  Database,
  DatabaseZap,
  FileCog,
  FileInput,
  FileKey,
  FilePen,
  FileSearch,
  FileSignature,
  FileStack,
  FileText,
  FlaskConical,
  FolderKanban,
  FolderOpen,
  History,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Link2,
  ListChecks,
  MessageSquare,
  MessagesSquare,
  Network,
  Plug,
  Scale,
  ScanFace,
  ScanLine,
  ScanText,
  ScrollText,
  Settings,
  Settings2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Stamp,
  TextSelect,
  TrendingUp,
  Upload,
  UserCog,
  Users,
  Wand2,
  Webhook,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { KhoaDich } from '../../i18n';
import { QUYEN_MODULE } from '../../modules/module-to-chuc/constants';
import type { PhamViCong } from '../../lib/pham-vi-cong';

export interface NavItem {
  /**
   * Khoá dịch của nhãn, **không phải** nhãn.
   *
   * Nhãn là thứ đang được dịch, nên lấy nó làm khoá thì đổi chữ là đổi khoá.
   * Nhãn còn trùng nhau giữa các nhóm (`Tổng quan` có ở 5 nhóm) nên khoá theo
   * nhãn cũng không phân biệt được hai mục khác nhau. Khoá đặt theo đường dẫn.
   */
  khoa: KhoaDich;
  /**
   * Nhãn hiện nguyên văn, bỏ qua `khoa`.
   *
   * Chỉ dành cho mục sinh từ **dữ liệu lúc chạy** — hôm nay là danh sách bot.
   * Tên bot do người dùng đặt nên không có trong catalog và **không được dịch**:
   * một bot tên `Trợ lí Kế toán` phải giữ nguyên tên đó ở cả hai ngôn ngữ.
   */
  nhanThang?: string;
  href?: string;
  icon: LucideIcon;
  /** Mục cha: chỉ là công tắc gập, `href` của chính nó bị bỏ qua. */
  children?: NavItem[];
  /** Khớp cả route con không có mục nav riêng (`/xu-ly/nhan-dang/:ma`, `/xu-ly/chung-tu/:id`, `/xu-ly/cau-hinh-ocr/:id`). */
  matchPrefix?: boolean;
  /**
   * Chỉ hiện ở bản cài on-premise (`/config.json` báo `deploymentMode:
   * ON_PREMISE`) và với quản trị viên bản cài — màn license cục bộ (ADR-0058
   * mục 8). Bản SaaS không có API cục bộ nào cho màn này gọi.
   */
  chiOnPremise?: boolean;
  /**
   * Hiện khi người dùng có ít nhất một mã quyền ở đây — cho màn của nền tảng không gắn dịch vụ nào, như màn Module
   * (`core:module:*`, ADR-0104). Quản trị tổ chức có sẵn mọi mã.
   */
  quyen?: readonly string[];
}

export interface NavSection {
  /**
   * Khoá dịch của tên nhóm. Nhóm sinh lúc chạy (module đã cài, ADR-0096) mang id `module:{code}` thay khoá
   * dịch, vẫn dùng làm key và để nhớ trạng thái gập; tên hiện ra lấy ở `nhanNhomThang`.
   */
  khoaNhom: KhoaDich | `module:${string}`;
  /** Tên nhóm hiện nguyên văn, chỉ cho nhóm sinh lúc chạy: tên module do bên nộp gói đặt, không dịch. */
  nhanNhomThang?: string;
  items: NavItem[];
  /**
   * Cổng mà nhóm này thuộc về (`lib/pham-vi-cong`).
   *
   * Mọi nhóm trong tệp này là nghiệp vụ của một tổ chức khách hàng nên đều là
   * `tenant`; nhóm `QUẢN TRỊ NỀN TẢNG` không nằm ở đây mà sinh từ
   * `app/modules.ts`. Trường là **bắt buộc**: thêm một nhóm mà quên khai phạm
   * vi thì `tsc` báo ngay, thay vì nhóm đó lặng lẽ hiện ở cả hai cổng.
   */
  phamVi: PhamViCong;
  /**
   * Mã dịch vụ của nhóm (ADR-0019 mục 2.1): một dịch vụ **là** một nhóm điều
   * hướng. Đây là nơi duy nhất nối hai thứ; `lib/dich-vu-cong.ts` đọc nó để ẩn
   * nhóm ngoài gói và chặn nhánh URL của nhóm đó (ADR-0034).
   *
   * Bỏ trống thì nhóm không thuộc gói nào và giữ luật quyền cũ.
   */
  dichVu?: string;
}

/**
 * Tông màu của biểu tượng và tiêu đề nhóm trong thanh điều hướng.
 *
 * **Một màu nhấn duy nhất cho mọi nhóm** (ADR-UI-0011), thay cho bảng mười tông
 * phân loại ở §4.3 và §5 của đặc tả. Lý do: màu trong thanh điều hướng chỉ nên
 * trả lời "tôi đang ở đâu", còn mười màu cố định thì tranh chỗ với màu trạng
 * thái trong màn hình, và không đi theo màu nhấn người dùng tự chọn
 * (ADR-UI-0007).
 *
 * `primary-600` đọc theo biến token nên tự đổi cùng màu nhấn và cùng chủ đề.
 * Hằng này không nhận tham số: cần đổi màu toàn thanh thì sửa đúng một dòng.
 */
export const SECTION_TONE = 'text-primary-600';

/**
 * Các route **có thật và vào được bằng URL** nhưng bị gỡ khỏi thanh điều hướng
 * (§4.3). Khớp `href` **chính xác**, không khớp tiền tố: khớp tiền tố trên
 * `/tro-ly-ai` sẽ xóa sạch cả mục đó. Mục nào rỗng sau khi lọc thì bị bỏ hẳn.
 */
export const HIDDEN_HREFS: readonly string[] = [
  '/thong-bao',
  '/tri-thuc/ket-noi',
  '/tri-thuc/ocr-chuan-hoa',
  '/tro-ly-ai',
];

/**
 * Cây điều hướng đầy đủ (§6.1).
 *
 * Mục mang **khoá dịch**, không mang nhãn — ADR-UI-0010 nới quy ước "giữ
 * nguyên văn" của ADR-UI-0003 mục 6. Thêm một mục thì thêm khoá ở đây **và**
 * một dòng trong `nav.muc` của cả hai catalog; `check:i18n` đối chiếu hai bên.
 *
 * ⚠️ Ràng buộc cứng của thanh điều hướng này: một mục cha có `children` **chỉ
 * là** công tắc gập — `href` của chính nó bị bỏ qua. Vì vậy **không mục cha nào
 * được là một trang**.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    khoaNhom: 'nav.nhom.tongQuan',
    phamVi: 'tenant',
    dichVu: 'tong-quan',
    items: [
      { khoa: 'nav.muc.dashboard', href: '/', icon: LayoutDashboard },
      { khoa: 'nav.muc.baoCaoThongKe', href: '/bao-cao', icon: BarChart2 },
      { khoa: 'nav.muc.thongBao', href: '/thong-bao', icon: Bell },
    ],
  },
  {
    khoaNhom: 'nav.nhom.cauHinhHeThong',
    phamVi: 'tenant',
    dichVu: 'cau-hinh-he-thong',
    items: [
      {
        khoa: 'nav.muc.cauHinhHeThong',
        icon: Settings,
        children: [
          { khoa: 'nav.muc.cauHinhVaiTro', href: '/he-thong/vai-tro', icon: Shield },
          { khoa: 'nav.muc.cauHinhNguoiDung', href: '/he-thong/nguoi-dung', icon: Users },
          { khoa: 'nav.muc.coCauToChuc', href: '/he-thong/to-chuc', icon: Network },
          // ADR-0059 mục 8 (thay màn MFA và IP allowlist của ADR-0015): Cài đặt bảo mật của tenant.
          { khoa: 'nav.muc.caiDatBaoMat', href: '/he-thong/bao-mat', icon: ShieldCheck },
          // ADR-0097 mục 2: Tenant Admin tự gán domain thương hiệu cho cổng của tổ chức.
          { khoa: 'nav.muc.diaChiRieng', href: '/he-thong/dia-chi-rieng', icon: Link2 },
          // ADR-0068: khoá API cho hệ thống bên ngoài gọi API của module (G2-D1-18).
          { khoa: 'nav.muc.khoaApiToChuc', href: '/he-thong/khoa-api', icon: KeyRound },
          // ADR-0102: trung tâm thông báo của tổ chức — kênh nhận tin, tác vụ cảnh báo, nhật ký gửi.
          { khoa: 'canhBao.nhanNavTenant', href: '/he-thong/canh-bao', icon: BellRing },
          // ADR-0058 mục 8 "Phía khách": license của bản cài on-premise.
          { khoa: 'nav.muc.licenseCucBo', href: '/he-thong/license', icon: FileKey, chiOnPremise: true },
        ],
      },
    ],
  },
  {
    khoaNhom: 'nav.nhom.triThucAi',
    phamVi: 'tenant',
    dichVu: 'tri-thuc-ai',
    items: [
      { khoa: 'nav.muc.quanLyTriThuc', href: '/tri-thuc', icon: Brain },
      { khoa: 'nav.muc.kiemDuyetPheDuyet', href: '/tri-thuc/kiem-duyet', icon: CheckCircle },
      {
        khoa: 'nav.muc.cauHinhDauVaoTriThuc',
        icon: Settings2,
        children: [
          { khoa: 'nav.muc.uploadTaiLieu', href: '/tri-thuc/upload', icon: Upload },
          { khoa: 'nav.muc.ketNoiDuLieuTuDong', href: '/tri-thuc/ket-noi', icon: Link2 },
          { khoa: 'nav.muc.ocrChuanHoa', href: '/tri-thuc/ocr-chuan-hoa', icon: Wand2 },
        ],
      },
    ],
  },
  {
    khoaNhom: 'nav.nhom.xuLyTaiLieu',
    phamVi: 'tenant',
    dichVu: 'xu-ly-tai-lieu',
    items: [
      { khoa: 'nav.muc.nhanDangOcr', href: '/xu-ly/nhan-dang', icon: ScanLine, matchPrefix: true },
      { khoa: 'nav.muc.quanLyTaiLieuOcr', href: '/xu-ly/tai-lieu-ocr', icon: FileSearch },
      { khoa: 'nav.muc.quanLyChungTu', href: '/xu-ly/chung-tu', icon: FolderOpen, matchPrefix: true },
      /*
       * Cấu hình của dịch vụ nằm trong nhóm của dịch vụ đó, không ở "Cấu hình hệ thống" (ADR-UI-0015): nhóm
       * Cấu hình hệ thống là dịch vụ bắt buộc nên mọi tenant đều thấy, kể cả tenant không thuê OCR.
       */
      {
        khoa: 'nav.muc.cauHinhOcr',
        icon: ScanText,
        children: [
          { khoa: 'nav.muc.thietLapChungTuOcr', href: '/xu-ly/cau-hinh-ocr', icon: FileText, matchPrefix: true },
        ],
      },
    ],
  },
  {
    khoaNhom: 'nav.nhom.xuLyVanBan',
    phamVi: 'tenant',
    dichVu: 'xu-ly-van-ban',
    items: [
      {
        khoa: 'nav.muc.xuLyVanBan',
        icon: FileSignature,
        children: [
          { khoa: 'nav.muc.tomTatVanBan', href: '/xu-ly-van-ban/tom-tat', icon: TextSelect },
          { khoa: 'nav.muc.soanThaoVanBan', href: '/xu-ly-van-ban/soan-thao', icon: FilePen },
          { khoa: 'nav.muc.phanCongNhiemVu', href: '/xu-ly-van-ban/phan-cong', icon: ListChecks },
          {
            khoa: 'nav.muc.quanLyNhiemVu',
            href: '/xu-ly-van-ban/quan-ly-nhiem-vu',
            icon: ClipboardList,
          },
        ],
      },
      // Cấu hình của dịch vụ Xử lý văn bản, chuyển từ nhóm Cấu hình hệ thống sang đây (ADR-UI-0015).
      {
        khoa: 'nav.muc.cauHinhXuLyVanBan',
        icon: FileCog,
        children: [
          { khoa: 'nav.muc.cauHinhDauVaoVb', href: '/xu-ly-van-ban/cau-hinh/dau-vao', icon: FileInput },
          { khoa: 'nav.muc.danhSachMauVanBan', href: '/xu-ly-van-ban/cau-hinh/mau-van-ban', icon: LayoutTemplate },
          { khoa: 'nav.muc.vaiTroCacCap', href: '/xu-ly-van-ban/cau-hinh/vai-tro-cac-cap', icon: Network },
        ],
      },
    ],
  },
  {
    khoaNhom: 'nav.nhom.troLiAi',
    phamVi: 'tenant',
    dichVu: 'tro-ly-ai',
    items: [
      { khoa: 'nav.muc.tongQuan', href: '/tro-ly-ai/tong-quan', icon: LayoutDashboard },
      { khoa: 'nav.muc.hoiDapAi', href: '/tro-ly-ai', icon: Sparkles },
      // Lưới bot dùng được (ADR-0086, như native). Màn trò chuyện của một bot là
      // `/tro-ly-ai/tro-chuyen/<id>`, nằm dưới `href` nên `matchPrefix` giữ mục sáng.
      { khoa: 'nav.muc.troChuyenVoiBot', href: '/tro-ly-ai/tro-chuyen', icon: MessagesSquare, matchPrefix: true },
      {
        khoa: 'nav.muc.nguonDuLieu',
        icon: Database,
        children: [
          { khoa: 'nav.muc.ketNoiNguon', href: '/tro-ly-ai/ket-noi', icon: Plug },
          { khoa: 'nav.muc.khoDuLieu', href: '/tro-ly-ai/ban-sao', icon: DatabaseZap, matchPrefix: true },
          // Công cụ API của native đã thành khuôn API ở Truy vấn mẫu; `/cong-cu-api` chuyển hướng sang đây.
          { khoa: 'nav.muc.truyVanMau', href: '/tro-ly-ai/khuon', icon: Workflow, matchPrefix: true },
          { khoa: 'nav.muc.phanQuyenDuLieu', href: '/tro-ly-ai/quyen-du-lieu', icon: ShieldCheck },
        ],
      },
      { khoa: 'nav.muc.cauHinhChatbot', href: '/tro-ly-ai/chatbot', icon: Bot },
      {
        khoa: 'nav.muc.vanHanh',
        icon: Activity,
        children: [
          { khoa: 'nav.muc.nhatKyTruyVet', href: '/tro-ly-ai/nhat-ky', icon: ScrollText },
          { khoa: 'nav.muc.thuNghiem', href: '/tro-ly-ai/thu-nghiem', icon: FlaskConical },
        ],
      },
    ],
  },
  {
    khoaNhom: 'nav.nhom.thamDinhAi',
    phamVi: 'tenant',
    dichVu: 'tham-dinh-ai',
    items: [
      { khoa: 'nav.muc.tongQuan', href: '/tham-dinh', icon: LayoutDashboard },
      { khoa: 'nav.muc.hoSoThamDinh', href: '/tham-dinh/ho-so', icon: ClipboardCheck },
      { khoa: 'nav.muc.pheDuyet', href: '/tham-dinh/phe-duyet', icon: Stamp },
      { khoa: 'nav.muc.thuVienTieuChi', href: '/tham-dinh/tieu-chi', icon: Scale },
      { khoa: 'nav.muc.tichHopApi', href: '/tham-dinh/tich-hop', icon: Webhook },
    ],
  },
  {
    khoaNhom: 'nav.nhom.dinhDanhEkyc',
    phamVi: 'tenant',
    dichVu: 'dinh-danh-ekyc',
    items: [
      { khoa: 'nav.muc.tongQuan', href: '/ekyc', icon: LayoutDashboard },
      { khoa: 'nav.muc.dinhDanhNguoiDung', href: '/ekyc/dinh-danh', icon: BadgeCheck },
      { khoa: 'nav.muc.hoSoDinhDanh', href: '/ekyc/ho-so', icon: ScanFace },
      { khoa: 'nav.muc.quyTrinhDinhDanh', href: '/ekyc/quy-trinh', icon: Workflow },
      { khoa: 'nav.muc.ungDungKhoaApi', href: '/ekyc/ung-dung', icon: KeyRound },
    ],
  },
  {
    khoaNhom: 'nav.nhom.baoCaoDoanhNghiep',
    phamVi: 'tenant',
    dichVu: 'bao-cao-doanh-nghiep',
    items: [
      {
        khoa: 'nav.muc.phanHe',
        icon: LayoutGrid,
        children: [
          {
            khoa: 'nav.muc.tongQuan',
            href: '/bao-cao-doanh-nghiep/tong-quan',
            icon: LayoutDashboard,
          },
          { khoa: 'nav.muc.taiChinh', href: '/bao-cao-doanh-nghiep/tai-chinh', icon: Banknote },
          { khoa: 'nav.muc.duAn', href: '/bao-cao-doanh-nghiep/du-an', icon: FolderKanban },
          { khoa: 'nav.muc.kinhDoanh', href: '/bao-cao-doanh-nghiep/kinh-doanh', icon: TrendingUp },
          { khoa: 'nav.muc.nhanSu', href: '/bao-cao-doanh-nghiep/nhan-su', icon: Users },
        ],
      },
      { khoa: 'nav.muc.taiLieuBaoCao', href: '/bao-cao-doanh-nghiep/tai-lieu', icon: FileStack },
      { khoa: 'nav.muc.nguonBaoCao', href: '/bao-cao-doanh-nghiep/nguon', icon: Database },
    ],
  },
  {
    khoaNhom: 'nav.nhom.quanLyRpa',
    phamVi: 'tenant',
    dichVu: 'quan-ly-rpa',
    items: [
      { khoa: 'nav.muc.dashboardRpa', href: '/rpa', icon: LayoutDashboard },
      { khoa: 'nav.muc.troLyAgentRpa', href: '/rpa/agent', icon: Sparkles },
      {
        khoa: 'nav.muc.quanLyProjectWorkflow',
        icon: Bot,
        children: [
          { khoa: 'nav.muc.danhSachProject', href: '/rpa/projects', icon: FolderKanban },
          { khoa: 'nav.muc.danhSachLuong', href: '/rpa/workflows', icon: Workflow },
          { khoa: 'nav.muc.lichSuLuongChay', href: '/rpa/history', icon: History },
          { khoa: 'nav.muc.baoCaoLoiSuCo', href: '/rpa/incidents', icon: AlertTriangle },
        ],
      },
      // ADR-0103: cấu hình lỗi và kết nối OpenCore của tenant (cổng tenant chưa có registry riêng, như bao-cao, tro-ly-ai).
      { khoa: 'nav.muc.cauHinhLoiKetNoi', href: '/rpa/alert-config', icon: ShieldAlert },
      { khoa: 'nav.muc.quanLyLicenseRpa', href: '/rpa/licenses', icon: KeyRound },
      { khoa: 'nav.muc.quanLyUserOpenCore', href: '/rpa/opencore-users', icon: UserCog },
    ],
  },
  {
    // ADR-0096 mục 8: nộp gói, xem và cài module đã duyệt. Không gắn dịch vụ: mọi tenant đều có. Module đã cài hiện
    // thành nhóm riêng ngay dưới nhóm này (Sidebar ghép thêm lúc chạy).
    khoaNhom: 'nav.nhom.module',
    phamVi: 'tenant',
    items: [
      {
        khoa: 'nav.muc.danhSachModule',
        href: '/module',
        icon: Blocks,
        quyen: [QUYEN_MODULE.xem, QUYEN_MODULE.nop, QUYEN_MODULE.cai],
      },
    ],
  },
];

/** Biểu tượng cho mục bot được chèn lúc chạy. */
export const BOT_ITEM_ICON = MessageSquare;
